import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from 'prop-types';
import TabContainer from "./TabContainer";
import { 
  createHandleSelect, 
  createHandleFilterChange, 
  createHandleStateChange, 
  createOnSort,
  createHandlePageSizeChange
} from "../../utils/commonHandlers";
import { WarningIcon } from "../../assets/icons";
import MessageModal from "./Modals/MessageModal";
import { setPageSizeForAll } from "../../slice/HomeSlice";

const GenericTabContainer = ({
  activeTab,
  handleTabChange,
  isLoading,
  isInitialLoad = false,
  getRowProps,
  setError,
  setShowNotification,
  handleError,
  setIsLoading,
  handleAPICalls,
  statusOptions,
  dueOnOptions,
  tabs,
  tabComponents,
  bellClickConfig,
  chipCountConfig
}) => {
  const dispatch = useDispatch();
  const { filterState } = useSelector((state) => state.home);
  const isRemindEmailEnabled = useSelector((state) => state.featureFlags.isRemindEmailEnabled);
  const [showWarningModal, setShowWarningModal] = useState(false)

  // Create handlers using utility functions
  const handleSelect = createHandleSelect(dispatch, activeTab, setIsLoading, handleAPICalls);
  const handleFilterChange = createHandleFilterChange(dispatch, activeTab, setIsLoading, handleAPICalls);
  const handleStateChange = createHandleStateChange(dispatch, activeTab);
  const onSort = createOnSort(dispatch, filterState, activeTab, setIsLoading, handleAPICalls);
  
  const handlePageSizeChange = createHandlePageSizeChange(
    dispatch,
    activeTab,
    handleAPICalls,
    setPageSizeForAll
  );

  const getChipCount = (chip) => {
    if (chipCountConfig?.[activeTab]) {
      return chipCountConfig[activeTab](chip);
    }
    return 0;
  };

  const handleCloseModal = () => {
    setShowWarningModal(false);
  }

  // Get the current tab component
  const CurrentTabComponent = tabComponents[activeTab];
  const currentPage = filterState?.[activeTab]?.page || 1;

  const handleBellClick = (record) => {
    if (!isRemindEmailEnabled) {
      setShowWarningModal(true);
      return;
    }
    bellClickConfig.handleBellClick(record);
  };

  return (
    <TabContainer
      activeTab={activeTab}
      handleTabChange={handleTabChange}
      isLoading={isLoading}
      isInitialLoad={isInitialLoad}
      filterState={filterState}
      handleSelect={handleSelect}
      handleFilterChange={handleFilterChange}
      handleStateChange={handleStateChange}
      handleChangePageSize={handlePageSizeChange}
      statusOptions={statusOptions}
      dueOnOptions={dueOnOptions}
      getChipCount={getChipCount}
      tabs={tabs}
    >
      <CurrentTabComponent
        currentPage={currentPage}
        getRowProps={getRowProps}
        handlePageChange={handleFilterChange}
        // Same function, named for intent: the Assignments tab changes
        // a filter (which side of the assignment to show) rather than a
        // page, and calling it handlePageChange there would mislead.
        handleFilterChange={handleFilterChange}
        onSort={onSort}
        handleBellClick={handleBellClick}
        filterState={filterState}
        handleChangePageSize={handlePageSizeChange}
        isLoading={isLoading}
        isInitialLoad={isInitialLoad}
        {...tabComponents.props}
      />
      <MessageModal
        open={showWarningModal}
        onClose={handleCloseModal}
        title={"Warning: E-Signature Remind Action is Disabled"}
        message={
          "E-Signature Remind Email notifications are currently disabled. Please contact your admin."
        }
        buttonText={"Close"}
        icon={<WarningIcon />}
        onClick={handleCloseModal}
        variant="warning"
      />
    </TabContainer>
  );
};

GenericTabContainer.propTypes = {
  activeTab: PropTypes.string.isRequired,
  handleTabChange: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  isInitialLoad: PropTypes.bool,
  getRowProps: PropTypes.func.isRequired,
  setError: PropTypes.func.isRequired,
  setShowNotification: PropTypes.func.isRequired,
  handleError: PropTypes.func.isRequired,
  setIsLoading: PropTypes.func.isRequired,
  handleAPICalls: PropTypes.func.isRequired,
  statusOptions: PropTypes.array.isRequired,
  dueOnOptions: PropTypes.array,
  tabs: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired
  })).isRequired,
  tabComponents: PropTypes.object.isRequired, // Object with tab keys mapping to components
  bellClickConfig: PropTypes.shape({
    handleBellClick: PropTypes.func
  }),
  chipCountConfig: PropTypes.object // Object with tab keys mapping to chip count functions
};

export default GenericTabContainer; 